import { action, computed, observable, runInAction } from "mobx";
import moment from "moment";
import { FormStore } from "@modules/formx";
import { isEmpty } from "lodash";
import {
  ConsultantEventLog_GetEvents as getConsultantEvents,
  ConsultantProfileForQualityItem_GetConsultant as getConsultant,
  ConsultantProfileForQualityItemFragment,
  ConsultantQualityItemEditor_ClearStatus as clearStatus,
  ConsultantQualityItemEditor_Get as getData,
  ManuallySetStatus_HD,
  ConsultantQualityItemEditor_Upload as upload,
  ConsultantQualityItemEditorFragment,
  ConsultantSearchCard_Get,
  ConsultantQuality_GetConsultantNew
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { FormData, schema, statusToMeta } from "../../quality-item-editor/form";
import { getUtcDate } from "../../quality-item-editor";
import { Snackbar } from "@bonliva-crm/ui";
import { MetaData } from "../../quality-item-editor/Statuses/StatusMeta";
import { getQIParamsIfITwasSet } from "@consultants/consultant-profile-new/quality-checks/components/quality-check/health-declaration-quality-check/utils";

type Data = ConsultantQualityItemEditorFragment;

export class HealthDeclarationQualityItemEditorStore {
  @observable data: Data;

  @observable consultant: ConsultantProfileForQualityItemFragment = null;
  @observable error = null;
  @observable loading = true;
  @observable isDocumentRequired = false;
  @observable withDocument = false;
  @observable fileToUpload: File = null;

  @observable metadata: MetaData = {};
  @observable metaCanSubmit = false;

  @computed get selectedScheme() {
    return schema;
  }

  @computed get name() {
    return this.data?.Name;
  }

  form = new FormStore<FormData>({ schema: this.selectedScheme, initialDirty: true });

  constructor(
    private consultantId: string,
    private country: string,
    private itemName: string,
    private onClose: () => void,
    private snackbar: Snackbar
  ) {
    this.fetch();
    this.fetchConsultant();
    (window as any).consultantQualityItemEditorStore = this;
  }

  get canUploadDocuments() {
    return store().user.can("consultant-health-declaration:update:all");
  }

  get canChangeStatus() {
    return store().user.can("consultant-health-declaration:update:all");
  }

  get canChangeComment() {
    return store().user.can("consultant-health-declaration:update:all");
  }

  get availableStatusNames() {
    return this.data ? this.data.AvailableStatuses.map((x) => x.Name) : [];
  }

  get selectedStatusIndex() {
    return this.form.values.selectedStatus
      ? this.data.AvailableStatuses.findIndex(
          (x) => x.Name === this.form.values.selectedStatus
        )
      : 0;
  }

  get statusOptions() {
    return this.data?.AvailableStatuses[this.selectedStatusIndex];
  }

  get canSubmit() {
    return (
      (this.form.canSubmit || this.metaCanSubmit) &&
      !this.isDocumentRequired &&
      this.canChangeStatus
    );
  }

  get isDateDisabled() {
    return !!this.data?.Status;
  }

  get canCancel() {
    return Boolean(this.data?.Status);
  }

  get isCommentRequired() {
    return this.statusOptions.IsCommentRequired;
  }

  get withComment() {
    return this.statusOptions.WithComment;
  }

  get commentLabel() {
    return locale.Comment;
  }

  requestUpload = () =>
    store()
      .upload.requestFileUpload({
        multiple: false
      })
      .then((files) => {
        this.fileToUpload = files[0];
      });

  handleUpload = async () => {
    if (!this.fileToUpload || !this.form.values.selectedStatus) {
      return;
    }

    this.loading = true;

    return upload
      .func({
        consultantId: this.consultantId,
        documentType: this.data.DocumentType,
        statusName: this.form.values.selectedStatus,
        file: this.fileToUpload
      })
      .then(() => this.fetch())
      .catch((err) => {
        this.snackbar.showError(`Uploading failed\nReason: ${err}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  closeUpload = () => (this.fileToUpload = null);

  submitTransition = (
    name:
      | "ManuallyHandle_ApprovedWithComment"
      | "ManuallyHandle_Approved"
      | "ManuallyHandle_RequestReceivedNotProcessed"
  ) => {
    const params = getQIParamsIfITwasSet(this.data, {
      setOn: this.form.values.date?.toISOString(),
      comment: this.metadata.comment
    });
    this.submit(
      ManuallySetStatus_HD.func(
        {
          consultantId: this.consultantId,
          qualityTemplate: this.data.templateName,
          transitionName: name,
          transitionParams: params
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName,
            ConsultantQuality_GetConsultantNew.OperationName
          ]
        }
      )
    );
  };

  clearStatus = () => {
    this.submit(
      clearStatus.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName,
            ConsultantQuality_GetConsultantNew.OperationName
          ]
        }
      )
    );
  };

  @action setStatus = async (statusName: string) => {
    if (!statusName) {
      return;
    }

    const status = this.data?.AvailableStatuses.find((x) => x.Name === statusName);
    // if (!status && !this.referenceId) {
    if (!status) {
      throw new Error(`Cannot find status ${status.Name}`);
    }

    this.form.setValue("selectedStatus", status.Name);

    if (status.Name === "RequestSent") {
      this.form.setValue("date", new Date());
    }
  };

  checkStatus = (statusName: string) => {
    if (!statusName) {
      return {
        disabled: false
      };
    }

    const status = this.data?.AvailableStatuses.find((x) => x.Name === statusName);

    return {
      disabled:
        status && status.WithDocument && status.IsDocumentRequired
          ? !this.fileToUpload
          : false
    };
  };

  setStatusMetaData(metadata: MetaData) {
    this.metadata = metadata;
  }

  setStatusCanSubmit(canSubmit: boolean) {
    this.metaCanSubmit = canSubmit;
  }

  private fetch = (refetch = false) => {
    this.loading = true;

    return getData
      .func(
        {
          consultantId: this.consultantId,
          country: this.country,
          itemName: this.itemName
        },
        { fetchPolicy: "network-only" }
      )
      .then(({ data }) => {
        runInAction(() => {
          const prevStatus = this.form.values.selectedStatus;
          this.setData(data.getConsultantQualityItem);
          this.setStatus(prevStatus);
        });
      })
      .catch((err) => {
        this.snackbar.showError(`Cannot fetch quality item`);
        console.error(err);
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      });
  };

  @action private setData = (data: ConsultantQualityItemEditorFragment) => {
    this.data = data;
    this.withDocument = this.data.AvailableStatuses.some((x) => x.WithDocument);

    // if there is a status - date should be treat as initial value
    // otherwise is just value (form is dirty)
    if (this.data.Status) {
      this.form.reset({
        date: moment(
          this.form.dirty && this.form.values.selectedStatus === this.data.Status.Name
            ? this.form.values.date
            : getUtcDate(this.data.Status.SetOn)
        ).toDate(),
        selectedStatus: this.data.Status.Name
      });

      this.metadata = statusToMeta(this.data.Status);
    } else {
      this.form.setValue("date", this.form.dirty ? this.form.values.date : getUtcDate());

      if (!isEmpty(this.data.AvailableStatuses)) {
        this.setStatus(this.data.AvailableStatuses[0].Name);
      }
    }
  };

  private submit = (promise: Promise<any>) => {
    this.loading = true;

    promise
      .then(() => {
        this.snackbar.showSuccess(locale.Saved);
        this.onClose();
      })
      .catch((err) => {
        this.snackbar.showError(`Cannot save.\nReason: ${err}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  private fetchConsultant() {
    getConsultant
      .func({
        id: this.consultantId
      })
      .then(({ data }) => {
        this.consultant = data.consultantById;
      })
      .catch((e) => console.log(e));
  }
}
